(() => {
  if (customElements.get('cart-shipping-estimator')) return;

  const CACHE_TTL_MS = 5 * 60 * 1000;
  const ADDRESS_FAILURE_COOLDOWN_MS = 60 * 1000;
  const HARD_TIMEOUT_MS = 11 * 1000;
  const POLL_DELAYS_MS = [1500, 2500, 3500];
  const LOCK_TTL_MS = 15 * 1000;
  const MAX_ATTEMPTS_10_MIN = 5;
  const MAX_ATTEMPTS_24_HOURS = 25;
  const CIRCUIT_WINDOW_MS = 10 * 60 * 1000;
  const CIRCUIT_OPEN_MS = 30 * 60 * 1000;
  const CONFLICT_COOLDOWN_MS = 60 * 1000;

  const STORAGE_PREFIX = 'horacio-cart-shipping-v2';
  const ADDRESS_KEY = `${STORAGE_PREFIX}|address`;
  const ROLLOUT_KEY = `${STORAGE_PREFIX}|rollout-bucket`;
  const QUOTA_KEY = `${STORAGE_PREFIX}|attempts`;
  const LOCK_KEY = `${STORAGE_PREFIX}|lock`;
  const CIRCUIT_KEY = `${STORAGE_PREFIX}|circuit`;

  const STATE_NAMES = {
    ACT: 'Australian Capital Territory',
    NSW: 'New South Wales',
    NT: 'Northern Territory',
    QLD: 'Queensland',
    SA: 'South Australia',
    TAS: 'Tasmania',
    VIC: 'Victoria',
    WA: 'Western Australia',
  };

  class ShippingRequestError extends Error {
    constructor(message, status = 0) {
      super(message);
      this.name = 'ShippingRequestError';
      this.status = status;
    }
  }

  const abortError = () => new DOMException('Shipping estimate aborted', 'AbortError');

  const sleep = (milliseconds, signal) =>
    new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(abortError());
        return;
      }

      const timeoutId = window.setTimeout(resolve, milliseconds);
      signal?.addEventListener(
        'abort',
        () => {
          window.clearTimeout(timeoutId);
          reject(abortError());
        },
        { once: true }
      );
    });

  const safeParse = (value, fallback) => {
    // localStorage.getItem returns null for a key that was never written, and
    // JSON.parse(null) yields null rather than throwing - so the catch below never
    // fires and callers used to receive null instead of the fallback.
    if (value === null || value === undefined || value === '') return fallback;
    try {
      const parsed = JSON.parse(value);
      return parsed === null || parsed === undefined ? fallback : parsed;
    } catch (_error) {
      return fallback;
    }
  };

  const randomId = () =>
    window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  class CartShippingEstimator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      if (!this.isIncludedInRollout()) {
        this.hidden = true;
        return;
      }

      this.form = this.querySelector('.cart-shipping-estimator__form');
      this.postcode = this.querySelector('[name="postcode"]');
      this.suburb = this.querySelector('[name="suburb"]');
      this.button = this.querySelector('.cart-shipping-estimator__button');
      this.buttonLabel = this.querySelector('[data-label]');
      this.status = this.querySelector('.cart-shipping-estimator__status');
      this.results = this.querySelector('.cart-shipping-estimator__results');
      this.help = this.querySelector('.cart-shipping-estimator__help');
      this.postcodeAssets = safeParse(this.dataset.postcodeAssets || '{}', {});

      if (!this.form || !this.postcode || !this.suburb || !this.button || !this.status || !this.results) {
        this.hidden = true;
        return;
      }

      this.boundPostcodeInput = this.handlePostcodeInput.bind(this);
      this.boundSuburbChange = this.handleSuburbChange.bind(this);
      this.boundCheckout = this.handleCheckout.bind(this);
      this.boundInputFocus = this.handleInputFocus.bind(this);
      this.postcode.addEventListener('input', this.boundPostcodeInput);
      this.suburb.addEventListener('change', this.boundSuburbChange);
      this.postcode.addEventListener('focus', this.boundInputFocus);
      this.suburb.addEventListener('focus', this.boundInputFocus);
      document.getElementById('CartDrawer-Checkout')?.addEventListener('click', this.boundCheckout);

      this.restoreAddress();
    }

    disconnectedCallback() {
      this.postcode?.removeEventListener('input', this.boundPostcodeInput);
      this.suburb?.removeEventListener('change', this.boundSuburbChange);
      this.postcode?.removeEventListener('focus', this.boundInputFocus);
      this.suburb?.removeEventListener('focus', this.boundInputFocus);
      document.getElementById('CartDrawer-Checkout')?.removeEventListener('click', this.boundCheckout);
      this.abortRequests('disconnected');
      this.releaseLock();
    }

    isIncludedInRollout() {
      const rollout = Math.max(0, Math.min(100, Number(this.dataset.rollout || 0)));
      if (rollout <= 0) return false;
      if (rollout >= 100) return true;

      try {
        let bucket = Number(localStorage.getItem(ROLLOUT_KEY));
        if (!Number.isInteger(bucket) || bucket < 0 || bucket > 99) {
          bucket = Math.floor(Math.random() * 100);
          localStorage.setItem(ROLLOUT_KEY, String(bucket));
        }
        return bucket < rollout;
      } catch (_error) {
        return false;
      }
    }

    handleInputFocus(event) {
      window.setTimeout(() => {
        event.currentTarget?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      }, 350);
    }

    handleCheckout() {
      this.abortRequests('checkout');
      this.releaseLock();
    }

    abortRequests(reason) {
      this.abortReason = reason;
      this.postcodeController?.abort();
      this.abortController?.abort();
      if (this.hardTimeoutId) window.clearTimeout(this.hardTimeoutId);
      this.hardTimeoutId = null;
    }

    async handlePostcodeInput() {
      const postcode = this.postcode.value.replace(/\D/g, '').slice(0, 4);
      this.postcode.value = postcode;
      this.postcode.setAttribute('aria-invalid', 'false');
      this.postcodeController?.abort();
      this.abortRequests('address-changed');
      this.clearRates();
      this.resetSuburb('Enter postcode first');

      if (postcode.length !== 4) {
        this.hideStatus();
        this.updateButton();
        return;
      }

      await this.loadLocalities(postcode);
    }

    handleSuburbChange() {
      this.suburb.setAttribute('aria-invalid', 'false');
      this.clearRates();
      this.saveAddress();
      this.hideStatus();
      this.updateButton();
    }

    async loadLocalities(postcode, preferredSuburb = '', staleAfterCartChange = false) {
      const assetUrl = this.postcodeAssets[postcode[0]];
      if (!assetUrl) {
        this.showError('We could not load suburb suggestions right now.', true);
        return;
      }

      this.postcodeController?.abort();
      this.postcodeController = new AbortController();
      const timeoutId = window.setTimeout(() => this.postcodeController?.abort(), 3500);
      this.showStatus('Finding suburbs...', false);

      try {
        const response = await fetch(assetUrl, {
          credentials: 'same-origin',
          cache: 'force-cache',
          signal: this.postcodeController.signal,
        });
        if (!response.ok) throw new Error(`Postcode data failed (${response.status})`);

        const shard = await response.json();
        const localities = Array.isArray(shard[postcode]) ? shard[postcode] : [];
        if (!localities.length) {
          this.postcode.setAttribute('aria-invalid', 'true');
          this.showError('We could not match that postcode. Check it and try again.', true);
          return;
        }

        this.suburb.replaceChildren(new Option('Select suburb', ''));
        localities.forEach((locality) => {
          const option = new Option(`${locality.suburb}, ${locality.state}`, locality.suburb);
          option.dataset.state = locality.state;
          option.dataset.stateName = STATE_NAMES[locality.state] || locality.state;
          this.suburb.append(option);
        });
        this.suburb.disabled = false;

        const preferredOption = [...this.suburb.options].find(
          (option) => option.value.toLowerCase() === preferredSuburb.toLowerCase()
        );
        if (preferredOption) preferredOption.selected = true;
        else if (localities.length === 1) this.suburb.selectedIndex = 1;

        if (staleAfterCartChange && this.suburb.value) {
          this.showStatus('Cart updated — check delivery again.', false);
        } else {
          this.hideStatus();
        }
        this.saveAddress();
      } catch (error) {
        if (error?.name !== 'AbortError' || this.abortReason !== 'address-changed') {
          this.showError('We could not load suburb suggestions right now.', true);
        }
      } finally {
        window.clearTimeout(timeoutId);
        this.postcodeController = null;
        this.updateButton();
      }
    }

    resetSuburb(label) {
      this.suburb.replaceChildren(new Option(label, ''));
      this.suburb.disabled = true;
      this.suburb.setAttribute('aria-invalid', 'false');
    }

    currentAddress() {
      const selected = this.suburb.selectedOptions[0];
      return {
        postcode: this.postcode.value.trim(),
        suburb: selected?.value?.trim() || '',
        state: selected?.dataset.stateName || '',
      };
    }

    saveAddress() {
      const address = this.currentAddress();
      if (!/^\d{4}$/.test(address.postcode) || !address.suburb) return;
      try {
        sessionStorage.setItem(
          ADDRESS_KEY,
          JSON.stringify({ ...address, cartSignature: this.dataset.cartSignature || '' })
        );
      } catch (_error) {
        // Address persistence is optional.
      }
    }

    restoreAddress() {
      let saved = null;
      try {
        saved = safeParse(sessionStorage.getItem(ADDRESS_KEY), null);
      } catch (_error) {
        return;
      }
      if (!saved || !/^\d{4}$/.test(saved.postcode || '')) return;

      this.postcode.value = saved.postcode;
      const stale = saved.cartSignature && saved.cartSignature !== (this.dataset.cartSignature || '');
      this.loadLocalities(saved.postcode, saved.suburb || '', stale);
    }

    updateButton() {
      const address = this.currentAddress();
      const ready = /^\d{4}$/.test(address.postcode) && Boolean(address.suburb && address.state);
      this.button.disabled = !ready || this.loading;
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.loading) return;

      const address = this.currentAddress();
      if (!/^\d{4}$/.test(address.postcode)) {
        this.postcode.setAttribute('aria-invalid', 'true');
        this.showError('Enter a valid four-digit Australian postcode.', false);
        return;
      }
      if (!address.suburb || !address.state) {
        this.suburb.setAttribute('aria-invalid', 'true');
        this.showError('Select a suburb before checking delivery.', false);
        return;
      }

      this.saveAddress();
      const cacheKey = this.getCacheKey(address);
      const cachedRates = this.readCache(cacheKey);
      if (cachedRates?.length) {
        this.renderRates(cachedRates);
        this.hideStatus();
        return;
      }

      const addressCooldown = this.getAddressCooldown(cacheKey);
      if (addressCooldown > 0) {
        this.showError(
          `Please wait ${Math.ceil(addressCooldown / 1000)} seconds before checking this address again.`,
          true
        );
        return;
      }

      const circuitRemaining = this.getCircuitRemaining();
      if (circuitRemaining > 0) {
        this.showError('Delivery estimates are temporarily paused in this browser.', true);
        return;
      }

      const quota = this.getQuotaState();
      if (!quota.allowed) {
        this.showError('Too many delivery checks were made in this browser. Please try again later.', true);
        return;
      }

      if (!this.acquireLock()) {
        this.showError('A delivery check is already running in another tab. Please wait a moment.', false);
        return;
      }

      this.recordQuotaAttempt(quota.attempts);
      this.recordAddressAttempt(cacheKey);
      this.setLoading(true);
      this.clearRates();
      this.showStatus('Checking delivery rates...', false);
      this.abortReason = null;
      this.abortController = new AbortController();
      this.hardTimeoutId = window.setTimeout(() => {
        this.abortReason = 'timeout';
        this.abortController?.abort();
      }, HARD_TIMEOUT_MS);

      try {
        const rates = await this.fetchRates(address, this.abortController.signal);
        if (rates.length) {
          this.writeCache(cacheKey, rates);
          this.renderRates(rates);
          this.hideStatus();
          this.clearCircuitFailures();
        } else {
          this.showError(
            'We could not find a delivery estimate for this address. Check the postcode and suburb.',
            true
          );
        }
      } catch (error) {
        if (error?.name === 'AbortError') {
          if (this.isConnected && this.abortReason === 'timeout') {
            this.recordCircuitFailure(0, true);
            this.showError('Delivery estimates are taking longer than expected.', true);
          }
          return;
        }

        console.warn('[Horacio shipping estimator]', error);
        const status = Number(error?.status || 0);
        this.recordCircuitFailure(status, false);
        this.showError('Delivery estimates are temporarily unavailable.', true);
      } finally {
        if (this.hardTimeoutId) window.clearTimeout(this.hardTimeoutId);
        this.hardTimeoutId = null;
        this.abortController = null;
        this.releaseLock();
        if (this.isConnected) this.setLoading(false);
      }
    }

    async fetchRates(address, signal) {
      const rootUrl = this.dataset.rootUrl || window.Shopify?.routes?.root || '/';
      const root = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
      const query = new URLSearchParams({
        'shipping_address[country]': 'Australia',
        'shipping_address[province]': address.state,
        'shipping_address[city]': address.suburb,
        'shipping_address[zip]': address.postcode,
      });

      const prepareResponse = await fetch(
        `${root}cart/prepare_shipping_rates.json?${query.toString()}`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          signal,
        }
      );
      if (!prepareResponse.ok) {
        throw new ShippingRequestError(
          `Shipping preparation failed (${prepareResponse.status})`,
          prepareResponse.status
        );
      }

      for (const delay of POLL_DELAYS_MS) {
        if (!this.isConnected || signal.aborted) throw abortError();
        await sleep(delay, signal);
        if (!this.isConnected || signal.aborted) throw abortError();

        const ratesResponse = await fetch(
          `${root}cart/async_shipping_rates.json?${query.toString()}`,
          {
            method: 'GET',
            credentials: 'same-origin',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            signal,
          }
        );
        if (!ratesResponse.ok) {
          throw new ShippingRequestError(
            `Shipping result failed (${ratesResponse.status})`,
            ratesResponse.status
          );
        }

        const payload = await ratesResponse.json();
        if (payload && Array.isArray(payload.shipping_rates)) return payload.shipping_rates;
      }

      throw new ShippingRequestError('Shipping rates timed out');
    }

    renderRates(rates) {
      this.results.replaceChildren();
      rates.forEach((rate) => {
        const item = document.createElement('li');
        item.className = 'cart-shipping-estimator__result';

        const details = document.createElement('span');
        details.className = 'cart-shipping-estimator__result-details';
        const name = document.createElement('span');
        name.className = 'cart-shipping-estimator__result-name';
        name.textContent = rate.presentment_name || rate.name || 'Delivery';
        details.append(name);

        if (rate.delivery_date) {
          const date = document.createElement('small');
          date.className = 'cart-shipping-estimator__result-date';
          date.textContent = `Estimated delivery ${rate.delivery_date}`;
          details.append(date);
        }

        const price = document.createElement('strong');
        price.className = 'cart-shipping-estimator__result-price';
        price.textContent = this.formatPrice(rate.price, rate.currency);
        item.append(details, price);
        this.results.append(item);
      });
      this.results.hidden = !rates.length;
      if (this.help) this.help.hidden = true;
    }

    formatPrice(value, currency) {
      const amount = Number.parseFloat(value);
      if (Number.isNaN(amount)) return String(value || '');
      if (amount === 0) return 'Free';
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: currency || this.dataset.currency || 'AUD',
      }).format(amount);
    }

    getCacheKey(address) {
      return [
        `${STORAGE_PREFIX}|estimate`,
        this.dataset.cartSignature || 'unknown-cart',
        address.postcode,
        address.suburb.toLowerCase(),
        address.state,
      ].join('|');
    }

    readCache(key) {
      try {
        const cached = safeParse(sessionStorage.getItem(key), null);
        if (!cached || Date.now() - cached.createdAt > CACHE_TTL_MS) {
          sessionStorage.removeItem(key);
          return null;
        }
        return Array.isArray(cached.rates) ? cached.rates : null;
      } catch (_error) {
        return null;
      }
    }

    writeCache(key, rates) {
      try {
        sessionStorage.setItem(key, JSON.stringify({ createdAt: Date.now(), rates }));
      } catch (_error) {
        // The estimator still works when session storage is unavailable.
      }
    }

    addressAttemptKey(cacheKey) {
      return `${cacheKey}|last-attempt`;
    }

    recordAddressAttempt(cacheKey) {
      try {
        sessionStorage.setItem(this.addressAttemptKey(cacheKey), String(Date.now()));
      } catch (_error) {
        this.lastAttemptFallback = { key: cacheKey, time: Date.now() };
      }
    }

    getAddressCooldown(cacheKey) {
      let lastAttempt = 0;
      try {
        lastAttempt = Number(sessionStorage.getItem(this.addressAttemptKey(cacheKey)) || 0);
      } catch (_error) {
        if (this.lastAttemptFallback?.key === cacheKey) lastAttempt = this.lastAttemptFallback.time;
      }
      return lastAttempt
        ? Math.max(0, ADDRESS_FAILURE_COOLDOWN_MS - (Date.now() - lastAttempt))
        : 0;
    }

    getQuotaState() {
      try {
        const now = Date.now();
        const attempts = safeParse(localStorage.getItem(QUOTA_KEY), [])
          .map(Number)
          .filter((time) => Number.isFinite(time) && now - time < 24 * 60 * 60 * 1000);
        const attempts10Min = attempts.filter((time) => now - time < 10 * 60 * 1000);
        return {
          allowed: attempts10Min.length < MAX_ATTEMPTS_10_MIN && attempts.length < MAX_ATTEMPTS_24_HOURS,
          attempts,
        };
      } catch (_error) {
        return { allowed: true, attempts: [] };
      }
    }

    recordQuotaAttempt(attempts) {
      try {
        localStorage.setItem(QUOTA_KEY, JSON.stringify([...attempts, Date.now()]));
      } catch (_error) {
        // The in-component single-flight guard remains active.
      }
    }

    acquireLock() {
      try {
        const now = Date.now();
        const existing = safeParse(localStorage.getItem(LOCK_KEY), null);
        if (existing?.expiresAt > now) return false;

        this.lockId = randomId();
        localStorage.setItem(LOCK_KEY, JSON.stringify({ id: this.lockId, expiresAt: now + LOCK_TTL_MS }));
        const confirmed = safeParse(localStorage.getItem(LOCK_KEY), null);
        return confirmed?.id === this.lockId;
      } catch (_error) {
        this.lockId = 'memory-only';
        return true;
      }
    }

    releaseLock() {
      if (!this.lockId) return;
      try {
        const existing = safeParse(localStorage.getItem(LOCK_KEY), null);
        if (existing?.id === this.lockId) localStorage.removeItem(LOCK_KEY);
      } catch (_error) {
        // Nothing to release when local storage is unavailable.
      }
      this.lockId = null;
    }

    getCircuitState() {
      try {
        return safeParse(localStorage.getItem(CIRCUIT_KEY), { failures: [], openUntil: 0 });
      } catch (_error) {
        return { failures: [], openUntil: 0 };
      }
    }

    getCircuitRemaining() {
      const state = this.getCircuitState();
      return Math.max(0, Number(state.openUntil || 0) - Date.now());
    }

    recordCircuitFailure(status, timedOut) {
      const isConflict = status === 409;
      const isSevere = timedOut || status === 429 || status >= 500 || status === 0;
      if (!isConflict && !isSevere) return;

      try {
        const now = Date.now();
        const state = this.getCircuitState();
        const failures = (state.failures || [])
          .map(Number)
          .filter((time) => Number.isFinite(time) && now - time < CIRCUIT_WINDOW_MS);
        failures.push(now);
        const openUntil = isConflict
          ? now + CONFLICT_COOLDOWN_MS
          : failures.length >= 2
            ? now + CIRCUIT_OPEN_MS
            : Number(state.openUntil || 0);
        localStorage.setItem(CIRCUIT_KEY, JSON.stringify({ failures, openUntil }));
      } catch (_error) {
        // Circuit persistence is best-effort; the current request still stops.
      }
    }

    clearCircuitFailures() {
      try {
        localStorage.removeItem(CIRCUIT_KEY);
      } catch (_error) {
        // No-op.
      }
    }

    setLoading(loading) {
      this.loading = loading;
      this.button.disabled = loading;
      this.button.classList.toggle('is-loading', loading);
      this.buttonLabel.textContent = loading ? 'Checking...' : 'Check';
      this.form.setAttribute('aria-busy', String(loading));
      if (!loading) this.updateButton();
    }

    clearRates() {
      this.results.hidden = true;
      this.results.replaceChildren();
      if (this.help) this.help.hidden = true;
    }

    hideStatus() {
      this.status.hidden = true;
      this.status.textContent = '';
      this.status.classList.remove('is-error');
    }

    showStatus(message, isError) {
      this.status.textContent = message;
      this.status.hidden = false;
      this.status.classList.toggle('is-error', Boolean(isError));
      this.status.setAttribute('role', isError ? 'alert' : 'status');
    }

    showError(message, showHelp) {
      this.clearRates();
      this.showStatus(message, true);
      if (this.help) this.help.hidden = !showHelp;
    }
  }

  customElements.define('cart-shipping-estimator', CartShippingEstimator);
})();
